import * as React from 'react';
import { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import {
  AuthContext,
  AuthData,
  AuthState,
  AuthLogoutResult,
} from '@mc/react-auth';
import { getLoadDataError, LoadDataApiError } from '@mc/axios-api';
import { ErrorInfo } from '../components/ErrorInfo';
import { AuthApi } from '../api';
import { useErrorLogging } from '../components/ErrorLogging';

type AuthStatePart = Omit<AuthState, 'login' | 'logout'> & { isDirty: boolean };

export const ROUTE_LOGIN = '/login';

// const LOGGED_OUT_AUTH_DATA: AuthData = {
//   auth: false,
//   admin: false,
//   roleUzivatele: [],
// };
const LOGGED_OUT_AUTH_STATE: AuthStatePart = {
  isLoggedIn: false,
  // data: { ...LOGGED_OUT_AUTH_DATA },
  data: undefined,
  isDirty: true,
};

const NO_DATA_AUTH_STATE: AuthStatePart = {
  isLoggedIn: false,
  // data: { ...LOGGED_OUT_AUTH_DATA },
  data: undefined,
  isDirty: true,
};

export const AuthProvider: React.FC<React.PropsWithChildren<{
  renderCustomError?: (err: LoadDataApiError) => React.ReactNode;
}>> = ({ renderCustomError, children }) => {
  const history = useHistory();
  const errorLogging = useErrorLogging();

  const [dataError, setDataError] = useState<LoadDataApiError | undefined>(
    undefined
  );

  const [state, setState] = useState<AuthStatePart>(() => {
    // Init dat - nepřihlášen
    return {
      ...NO_DATA_AUTH_STATE,
    };
  });

  // Refresh dat pokud jsou dirty, např. přři prvním renderu nebyly načteny z window
  useEffect(() => {
    if (state.isDirty) {
      AuthApi.check()
        .then(res => {
          const authData = res.data;

          setState({
            isLoggedIn: authData.auth,
            data: authData,
            isDirty: false,
          });
          errorLogging.setLoggedUser(authData.login);
        })
        .catch(err => {
          let loadDataError = getLoadDataError(err);
          setDataError(loadDataError);
        });
    }
  }, [state.isDirty]);

  /**
   * Login akce - volá se po login na serveru a předavjí se sem data která server vrátil.
   */
  const login = React.useCallback((authData: AuthData) => {
    setState({
      isLoggedIn: authData.auth,
      data: authData,
      isDirty: false,
    });
    errorLogging.setLoggedUser(authData.login);
  }, []);

  /**
   * Logout, metoda neřeší error handling a je nutné ho vyřešit vně.
   */
  const logout = React.useCallback(() => {
    AuthApi.logout()
      .then(result => {
        try {
          document.cookie = '';
          errorLogging.setLoggedUser();
        } catch (e) {
          // ignore
        }

        const logoutResult: AuthLogoutResult =
          result?.data || AuthLogoutResult.SUCCESS;

        switch (logoutResult) {
          case AuthLogoutResult.REDIRECTED:
            // Už je přesměrováno
            return;
          case AuthLogoutResult.SUCCESS:
          default:
            setState({ ...LOGGED_OUT_AUTH_STATE });

            history.replace(ROUTE_LOGIN);
        }
      })
      .catch(err => {
        throw err;
      });
  }, []);

  const { isDirty, ...statePart } = state;

  // Interní uchování kvůli error boundary a logování app info - vím, že je to HACK
  // @ts-ignore
  window._auth = statePart;

  return (
    <AuthContext.Provider value={{ login, logout, ...statePart }}>
      {dataError &&
        (renderCustomError ? (
          renderCustomError(dataError)
        ) : (
          <ErrorInfo
            message={dataError.message}
            description={dataError.description}
            refreshAction
          />
        ))}
      {!dataError && !isDirty && children}
    </AuthContext.Provider>
  );
};
